import { useEffect } from "react";
import { Box, Typography, FormControl, InputLabel } from "@mui/material";
import { BUTTON_COLOR } from "../../../../types/coreButtonTypes";
import LabelInputField from "../../../../components/core-components/label-input-field/LabelInputField";
import { InputAdornment } from "@mui/material";
import { MUNICIPALITY_TAX_INCLUSIVE_STATUSES, VAT_INCLUSIVE_STATUSES } from "../../../../types/eventTypes";
import { addCommasInAmount } from "../../../../utils/commonUtils";
import { IQuoteCalculatedDetails, IQuoteFormFinancialDetails } from "../../../../types/quoteFormTypes";
import CoreButtons from "../../../core-components/button/CoreButtons";
import { municipalityTaxAmountData, vatInclusiveAmountData } from "../../../../utils/quoteFormUtils";
import {
	IQuoteDetailsFormErrorsState,
	IQuoteDetailsFormTouchedState,
} from "../../../../pages/QuoteForm/QuoteForm";
import "./quoteDetailsForm.scss";

interface IQuoteDetailsFormProps {
	financialFormData: IQuoteFormFinancialDetails;
	handleFieldTouched: (field: keyof IQuoteFormFinancialDetails) => void;
	onChangeFinancialValue: (value: string | number, field: keyof IQuoteFormFinancialDetails) => void;
	touchedFields: IQuoteDetailsFormTouchedState;
	formErrors: IQuoteDetailsFormErrorsState;
	calculatedValues: IQuoteCalculatedDetails;
}

const QuoteDetailsForm = ({
	financialFormData,
	handleFieldTouched,
	onChangeFinancialValue,
	touchedFields,
	formErrors,
	calculatedValues,
}: IQuoteDetailsFormProps) => {
	useEffect(() => {
		const blurNumberInputOnScroll = () => {
			if (document.activeElement instanceof HTMLInputElement && document.activeElement.type === "number") {
				document.activeElement.blur();
			}
		};
		document.addEventListener("wheel", blurNumberInputOnScroll);
		return () => {
			document.removeEventListener("wheel", blurNumberInputOnScroll);
		};
	}, []);

	return (
		<Box className="quoteDetailsForm">
			{/* Quote Amount */}
			<LabelInputField
				id="quoteAmount"
				type="number"
				value={financialFormData.amount as string}
				onBlur={() => handleFieldTouched("amount")}
				onChange={(e) => onChangeFinancialValue(e.currentTarget.value, "amount")}
				InputProps={{
					startAdornment: <InputAdornment position="start">AED</InputAdornment>,
				}}
				placeholder="0.00"
				required={true}
				label="Quote amount"
				error={!!(touchedFields.amount && formErrors.amount)}
				helperText={touchedFields.amount ? formErrors.amount : ""}
			/>

			{/* Municipality Tax Inclusion */}
			<FormControl variant="standard">
				<InputLabel htmlFor="municipalityTax" required={true}>
					Is the quote inclusive of Municipality tax?
				</InputLabel>
				<Box className="btnGroup" aria-label="municipalityTax">
					{municipalityTaxAmountData.map((buttonProps, index) => (
						<CoreButtons
							key={index}
							buttonText={buttonProps.buttonText}
							onclick={() => onChangeFinancialValue(buttonProps.value, "isMunicipalityTaxInclusive")}
							variant={buttonProps.variant}
							color={
								financialFormData.isMunicipalityTaxInclusive === buttonProps.value
									? BUTTON_COLOR.PRIMARY
									: BUTTON_COLOR.INHERIT
							}
							fullWidth={buttonProps.fullWidth}
							disabled={buttonProps.disabled}
							coreBtnPill={buttonProps.coreBtnPill}
							className={`buttonItem ${
								buttonProps.value === financialFormData.isMunicipalityTaxInclusive ? "active" : ""
							}`}
						/>
					))}
				</Box>
				<LabelInputField
					id="municipalityTax"
					type="number"
					value={financialFormData.municipalityTax as string}
					onChange={(e) => onChangeFinancialValue(e.currentTarget.value, "municipalityTax")}
					InputProps={{
						startAdornment: <InputAdornment position="start">AED</InputAdornment>,
					}}
					placeholder="0.00"
					required={
						financialFormData.isMunicipalityTaxInclusive !== MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable
					}
					error={!!(touchedFields.municipalityTax && formErrors.municipalityTax)}
					onBlur={() => handleFieldTouched("municipalityTax")}
					disabled={
						financialFormData.isMunicipalityTaxInclusive ===
							MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable ||
						!financialFormData.isMunicipalityTaxInclusive
					}
					label="Municipality tax amount"
					helperText={touchedFields.municipalityTax ? formErrors.municipalityTax : ""}
				/>
			</FormControl>

			{/* VAT Inclusion */}
			<FormControl variant="standard">
				<InputLabel htmlFor="vatAmount" required={true}>
					Is the quote VAT inclusive?
				</InputLabel>
				<Box className="btnGroup" aria-label="vatAmount">
					{vatInclusiveAmountData.map((buttonProps, index) => (
						<CoreButtons
							key={index}
							buttonText={buttonProps.buttonText}
							onclick={() => onChangeFinancialValue(buttonProps.value, "isVatInclusive")}
							variant={buttonProps.variant}
							color={
								financialFormData.isVatInclusive === buttonProps.value
									? BUTTON_COLOR.PRIMARY
									: BUTTON_COLOR.INHERIT
							}
							fullWidth={buttonProps.fullWidth}
							coreBtnPill={buttonProps.coreBtnPill}
							className={`buttonItem ${
								buttonProps.value === financialFormData.isVatInclusive ? "active" : ""
							}`}
						/>
					))}
				</Box>
				<LabelInputField
					id="vatAmount"
					type="number"
					value={calculatedValues.calculatedVat.toFixed(2)}
					InputProps={{
						startAdornment: <InputAdornment position="start">AED</InputAdornment>,
					}}
					placeholder="0.00"
					required={true}
					disabled
					label="VAT amount"
				/>
				{financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.NotRegistered && (
					<Typography className="vatNotRegistered">We will collect 5% VAT on your behalf</Typography>
				)}
			</FormControl>

			{/* Summary Section */}
			<Box className="totalSummary">
				<Box component="strong">Subtotal</Box>
				<Box component="strong" sx={{
					textAlign: "right"
				}}>
					AED {calculatedValues.subTotal ? addCommasInAmount(calculatedValues.subTotal) : "0.00"}
				</Box>
				<Box>Municipality tax</Box>
				<Box sx={{
					textAlign: "right"
				}}>
					AED{" "}
					{calculatedValues.municipalityTax ? addCommasInAmount(calculatedValues.municipalityTax) : "0.00"}
				</Box>
				<Box>VAT</Box>
				<Box sx={{
					textAlign: "right"
				}}>
					AED {calculatedValues.calculatedVat ? addCommasInAmount(calculatedValues.calculatedVat) : "0.00"}
				</Box>
				<Box component="strong">Total</Box>
				<Box component="strong" sx={{
					textAlign: "right"
				}}>
					AED {calculatedValues.total ? addCommasInAmount(calculatedValues.total) : "0.00"}
				</Box>
			</Box>
		</Box>
	);
};

export default QuoteDetailsForm;
